<template>
  <div class="inline-edit-container">
    <!-- Display Mode -->
    <transition name="edit-transition" mode="out-in" @after-enter="onTransitionComplete">
      <div
        v-if="!isEditing"
        key="display"
        class="title-display"
        @click="startEditing"
      >
        <h2 class="hero-title">{{ displayTitle }}</h2>
        <v-icon small class="edit-icon">mdi-pencil</v-icon>
      </div>

      <!-- Edit Mode -->
      <div
        v-else
        key="edit"
        class="title-edit"
        :class="{ 'is-saving': loading, 'save-success': showSuccess }"
      >
        <div class="input-wrapper">
          <input
            ref="titleInput"
            v-model="dsc_titulo_enquete"
            class="title-input"
            :disabled="loading"
            @keydown.enter="saveAndClose"
            @keydown.esc="cancelEditing"
            @blur="onBlur"
          />
          <div class="input-border"></div>
        </div>
        <div class="status-icon-container">
          <v-icon
            v-if="showSuccess"
            class="success-icon"
            color="primary"
            small
          >
            mdi-check-circle
          </v-icon>
          <v-progress-circular
            v-else-if="loading"
            indeterminate
            size="18"
            width="2"
            color="primary"
            class="loading-indicator"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions } from "vuex";

export default {
  props: {
    enquete: Object,
  },
  data() {
    return {
      loading: false,
      dsc_titulo_enquete: "",
      originalTitle: "",
      isEditing: false,
      showSuccess: false,
      timeoutIds: [],
    };
  },
  computed: {
    displayTitle() {
      return this.enquete?.dsc_titulo_enquete || "Título da enquete";
    },
  },
  created() {
    if (this.enquete !== null && this.enquete !== undefined) {
      this.dsc_titulo_enquete = this.enquete.dsc_titulo_enquete;
      this.originalTitle = this.enquete.dsc_titulo_enquete;
    }
  },
  methods: {
    ...mapActions({
      getEnquetesProducao: "enquetes/getEnquetesProducao",
      getEnqueteEmExibicao: "enquetes/getEnqueteEmExibicao",
    }),
    startEditing() {
      this.originalTitle = this.dsc_titulo_enquete;
      this.isEditing = true;
      this.showSuccess = false;
    },
    onTransitionComplete() {
      // Called after the edit input transition completes
      if (this.isEditing && this.$refs.titleInput) {
        this.$refs.titleInput.focus();
      }
    },
    cancelEditing() {
      this.dsc_titulo_enquete = this.originalTitle;
      this.isEditing = false;
      this.loading = false;
      this.showSuccess = false;
    },
    onBlur() {
      const timeoutId = setTimeout(() => {
        if (this.isEditing && !this.loading && !this.showSuccess) {
          this.cancelEditing();
        }
      }, 150);
      this.timeoutIds.push(timeoutId);
    },
    async saveAndClose() {
      if (this.loading) return;

      if (this.dsc_titulo_enquete.trim() === this.originalTitle?.trim()) {
        this.isEditing = false;
        return;
      }

      if (this.dsc_titulo_enquete.trim() === "") {
        this.$toast.error("O título não pode estar vazio.");
        this.dsc_titulo_enquete = this.originalTitle;
        this.isEditing = false;
        return;
      }

      await this.editarTituloEnquete();
    },
    async editarTituloEnquete() {
      try {
        this.loading = true;

        const payload = {
          dsc_titulo_enquete: this.dsc_titulo_enquete,
        };

        const cod_producao = this.$store.state.producoes.selectedProducao.cod_producao;

        const response = await apiClient.patch(
          `/producao/${cod_producao}/enquetes/${this.enquete.id}`,
          payload
        );

        if (response.status >= 200 && response.status < 300) {
          this.showSuccess = true;

          await Promise.all([
            this.getEnquetesProducao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
              filtro_texto: this.$store.state.enquetes.filtroEnquetes,
              size: 5,
              page: this.$store.state.enquetes.enquetesProducao.currentPage,
              cod_portal: this.$store.state.enquetes.selectedPortal,
              id_categoria: this.$store.state.enquetes.selectedCategoria,
            }),
            this.getEnqueteEmExibicao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
            }),
          ]);

          this.$emit('title-updated');
          this.originalTitle = this.dsc_titulo_enquete;

          const timeoutId = setTimeout(() => {
            this.isEditing = false;
            this.showSuccess = false;
            this.loading = false;
          }, 400);
          this.timeoutIds.push(timeoutId);
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.error || "Erro ao editar título");
        this.dsc_titulo_enquete = this.originalTitle;
        this.loading = false;
        this.isEditing = false;
      }
    },
  },
  beforeDestroy() {
    this.timeoutIds.forEach(id => clearTimeout(id));
  },
  watch: {
    enquete: {
      handler(newVal) {
        if (newVal && !this.isEditing) {
          this.dsc_titulo_enquete = newVal.dsc_titulo_enquete;
          this.originalTitle = newVal.dsc_titulo_enquete;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.inline-edit-container {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}

.title-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: var(--sc-radius-md);
  transition: all 0.25s var(--sc-easing-standard);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--sc-radius-md);
    background: rgba(255, 255, 255, 0);
    transition: background 0.25s var(--sc-easing-standard);
  }

  &:hover {
    &::before {
      background: var(--sc-surface-tint-hover);
    }

    .edit-icon {
      opacity: 0.8;
      transform: scale(1);
    }
  }

  &:active {
    transform: scale(0.98);
  }
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  margin: 0;
  line-height: 1.3;
  position: relative;
  z-index: 1;
}

.edit-icon {
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.25s var(--sc-easing-standard);
  color: var(--v-text_secondary-base);
  position: relative;
  z-index: 1;
}

.title-edit {
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &.is-saving .input-wrapper .title-input {
    opacity: 0.7;
  }

  &.save-success .input-wrapper .input-border {
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 3px rgba(var(--sc-color-primary-rgb),0.25);
  }
}

.input-wrapper {
  position: relative;
  display: inline-flex;
}

.title-input {
  background: var(--sc-surface-tint);
  border: none;
  border-radius: var(--sc-radius-md);
  padding: 8px 14px;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--v-text_primary-base);
  outline: none;
  transition: all 0.2s var(--sc-easing-standard);
  font-family: inherit;
  position: relative;
  z-index: 1;
  width: 400px;
  max-width: 100%;

  &:disabled {
    cursor: not-allowed;
  }
}

.input-border {
  position: absolute;
  inset: 0;
  border: 2px solid var(--v-primary-base);
  border-radius: var(--sc-radius-md);
  pointer-events: none;
  transition: all 0.3s var(--sc-easing-standard);
  animation: border-pulse 0.4s var(--sc-easing-standard);
}

@keyframes border-pulse {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.status-icon-container {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-indicator {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.success-icon {
  animation: success-pop 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes success-pop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Main transition */
.edit-transition-enter-active {
  animation: edit-in 0.25s var(--sc-easing-standard);
}

.edit-transition-leave-active {
  animation: edit-out 0.2s var(--sc-easing-standard);
}

@keyframes edit-in {
  0% {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes edit-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
}

</style>
